// ink-component
import React, { useContext } from 'react';
import Box from '../../../Box/Box';
import { BubbleProps } from '../../../Bubble/Bubble';
import { Tooltip } from '../../../Tooltip/Tooltip';
import { SideNavContext } from '../../SideNavContext';
import getGlobalProps from '../../../../helpers/constants/global-props';
import { IconProps } from '../../../Icon/Icon';
import { SideNavItemContent } from './SideNavItemContent';

export interface SideNavItemProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /** Link title text */
  title: string;
  /** URL to navigate to */
  url?: string;
  /** Active state modifier */
  isActive?: boolean;
  /** Nav item icon type */
  imageIcon?: IconProps['type'];
  /** Sticky state modifier */
  sticky?: boolean;
  /** Bubble type and text */
  bubble?: { type?: BubbleProps['type']; text: string | number } | null;
  /** Hides the vertical stripe in active items */
  noStripe?: boolean;
  /** Open link in new tab */
  openInNewTab?: boolean;
  /** Use React Router Link element instead of native anchor */
  withReactRouter?: boolean;
  /** onClick callback, sends the event */
  onClick?: React.HTMLAttributes<HTMLAnchorElement>['onClick'];
  /** Marks the element with a given ID to make it easier to select it on tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Navigation list item element (li)
 * @group SideNav
 * @categories navigation
 * @HTMLType HTMLAnchorElement
 */
const SideNavItem = React.forwardRef<HTMLAnchorElement, SideNavItemProps>((props, ref) => {
  const { isClosed } = useContext(SideNavContext);
  const { title, sticky, 'data-testid': dataTestId } = props;
  const globalProps = getGlobalProps({});

  if (!isClosed) {
    return (
      <Box minWidth={sticky ? 'stretch' : undefined} {...globalProps} data-testid={dataTestId}>
        <SideNavItemContent {...props} ref={ref} />
      </Box>
    );
  }

  return (
    <Box minWidth={sticky ? 'stretch' : undefined} {...globalProps} data-testid={dataTestId}>
      <Tooltip
        noDelay
        preventClickPersist
        triggerIsBlock
        isShowing={isClosed}
        trigger={<SideNavItemContent {...props} ref={ref} />}
      >
        {title}
      </Tooltip>
    </Box>
  );
});

SideNavItem.displayName = 'SideNav.Item';

export default SideNavItem;
